import React, { useState, useEffect, useRef } from 'react';

let globalTooltipActive = false;
let globalTooltipTimer = null;

const SmartTooltip = ({ children, content, width = '150px', disabled = false, position: forcedPosition = null }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [position, setPosition] = useState(forcedPosition || 'bottom');
    const buttonRef = useRef(null);
    const tooltipRef = useRef(null);
    const showTimerRef = useRef(null);
    const animationTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (showTimerRef.current) {
                clearTimeout(showTimerRef.current);
            }
            if (animationTimerRef.current) {
                clearTimeout(animationTimerRef.current);
            }
        };
    }, []);

    const calculatePosition = () => {
        if (!buttonRef.current) return 'bottom';

        const rect = buttonRef.current.getBoundingClientRect();
        const tooltipHeight = 100;
        const margin = 8;

        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceBelow >= tooltipHeight + margin) {
            return 'bottom';
        } else if (spaceAbove >= tooltipHeight + margin) {
            return 'top';
        }
        return 'bottom';
    };

    const handleMouseEnter = () => {
        if (disabled) return;

        if (showTimerRef.current) {
            clearTimeout(showTimerRef.current);
        }
        if (animationTimerRef.current) {
            clearTimeout(animationTimerRef.current);
        }

        const delay = globalTooltipActive ? 0 : 500;

        showTimerRef.current = setTimeout(() => {
            const pos = forcedPosition || calculatePosition();
            setPosition(pos);
            setShowTooltip(true);

            if (delay === 0) {
                setIsAnimating(true);
            } else {
                requestAnimationFrame(() => {
                    setIsAnimating(true);
                });
            }

            globalTooltipActive = true;

            if (globalTooltipTimer) {
                clearTimeout(globalTooltipTimer);
            }
        }, delay);
    };

    const handleMouseLeave = () => {
        if (showTimerRef.current) {
            clearTimeout(showTimerRef.current);
        }

        setIsAnimating(false);

        animationTimerRef.current = setTimeout(() => {
            setShowTooltip(false);
        }, 200);

        if (globalTooltipTimer) {
            clearTimeout(globalTooltipTimer);
        }

        globalTooltipTimer = setTimeout(() => {
            globalTooltipActive = false;
        }, 200);
    };

    return (
        <div className="relative" ref={buttonRef}>
            <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {children}
            </div>

            {showTooltip && !disabled && (
                <div
                    ref={tooltipRef}
                    className={`absolute left-1/2 transform -translate-x-1/2 ${
                        position === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2'
                    } px-3 py-2 bg-gray-800 text-white text-xs rounded-lg transition-all duration-200 ${
                        isAnimating
                            ? 'opacity-100 translate-y-0'
                            : position === 'bottom'
                                ? 'opacity-0 -translate-y-1'
                                : 'opacity-0 translate-y-1'
                    }`}
                    style={{ width, zIndex: 9999 }}
                >
                    {content}
                    <div
                        className={`absolute left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45 transition-opacity duration-200 ${
                            position === 'bottom' ? 'bottom-full -mb-1' : 'top-full -mt-1'
                        } ${isAnimating ? 'opacity-100' : 'opacity-0'}`}
                    />
                </div>
            )}
        </div>
    );
};

export default SmartTooltip;
